import React, { useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import AddOutlined from '@mui/icons-material/AddOutlined';
import { Button, FormConfirmDialog, FormSection } from '../../../../../components/ui';
import { useSnackBar } from '../../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import useSchedulerAutomations from '../../hooks/useSchedulerAutomations';
import SchedulerEditorDrawer from './SchedulerEditorDrawer';

const INITIAL_FORM = {
  action: 'update_status',
  trigger_delay: 1,
  trigger_delay_unit: 'days',
  description: '',
  active: true,
  frequency: 'Daily',
  sendTime: '09:00',
  template: { name: '', bot_number: '' },
};

function summarizeRule(item) {
  if (item.action === 'send_lead_summary') {
    const templateName = item.template?.name || item.notification_config?.template_id || '';
    return `Daily at ${item.sendTime || item.schedule_time || '09:00'}${templateName ? ` · ${templateName}` : ''}`.trim();
  }
  return `Move ${item.currentStatus?.status_name || 'status'} → ${item.nextStatus?.status_name || 'next'} after ${item.trigger_time || `${item.trigger_delay ?? ''} ${item.trigger_delay_unit ?? ''}`.trim()}`.trim();
}

function validateSchedulerForm(form) {
  if (form.action === 'update_status') {
    if (!form.currentStatus?.status_id) return 'Current status is required';
    if (!form.nextStatus?.status_id) return 'Next status is required';
    if (form.currentStatus?.status_id === form.nextStatus?.status_id) {
      return 'Next status must differ from current status';
    }
  }
  return '';
}

export default function ScheduledAutomationsSection({
  formId,
  workspaceId,
  hasFormId,
  organisationId = '',
}) {
  const { showSnackBar } = useSnackBar();
  const { semantic, typographyStyles, radiusPx } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.automations.scheduled;
  const saveCopy = FORM_STUDIO_COPY.automations;

  const automations = useSchedulerAutomations({ formId, workspaceId, enabled: hasFormId });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [deleteId, setDeleteId] = useState(null);

  if (!hasFormId) {
    return (
      <FormSection title={copy.title} description={copy.caption}>
        <Typography color="text.secondary">{saveCopy.saveFirst}</Typography>
      </FormSection>
    );
  }

  const handleSave = async () => {
    const validationError = validateSchedulerForm(form);
    if (validationError) {
      showSnackBar(validationError, 'warning');
      return;
    }

    const result = await automations.saveScheduler(form);
    showSnackBar(
      result.success ? copy.saveSuccess : copy.saveError,
      result.success ? 'success' : 'error'
    );
    if (result.success) {
      setDrawerOpen(false);
      setForm(INITIAL_FORM);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const result = await automations.deleteScheduler(deleteId);
    showSnackBar(
      result.success ? copy.deleteSuccess : copy.deleteError,
      result.success ? 'success' : 'error'
    );
    setDeleteId(null);
  };

  return (
    <FormSection title={copy.title} description={copy.caption}>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="primary"
          icon={<AddOutlined />}
          onClick={() => {
            setForm({
              ...INITIAL_FORM,
              campaign_id: workspaceId,
              lead_form_id: formId,
            });
            setDrawerOpen(true);
          }}
        >
          {copy.addAutomation}
        </Button>
      </Box>

      {automations.isLoading ? (
        <Typography sx={{ color: semantic.text.muted }}>{copy.loading}</Typography>
      ) : null}
      {automations.loadError ? (
        <Typography sx={{ color: semantic.states.danger }}>{automations.loadError}</Typography>
      ) : null}

      <Stack spacing={1.5}>
        {automations.schedulers.map((item) => (
          <Box
            key={String(item.scheduler_id ?? item.id ?? summarizeRule(item))}
            sx={{
              p: 2,
              borderRadius: radiusPx.lg,
              border: `1px solid ${semantic.borders.border}`,
              bgcolor: semantic.surfaces.surface,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Box>
                <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 }}>
                  {item.action === 'send_lead_summary' ? copy.leadSummaryLabel : copy.updateStatusLabel}
                </Typography>
                <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary }}>
                  {summarizeRule(item)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  label={item.active === false ? copy.inactive : copy.active}
                  color={item.active === false ? 'default' : 'success'}
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    setForm(item);
                    setDrawerOpen(true);
                  }}
                >
                  {copy.edit}
                </Button>
                <Button variant="outline" onClick={() => setDeleteId(item.scheduler_id)}>
                  {copy.delete}
                </Button>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>

      <SchedulerEditorDrawer
        open={drawerOpen}
        form={form}
        formId={formId}
        workspaceId={workspaceId}
        organisationId={organisationId}
        isSaving={automations.isSaving}
        onClose={() => setDrawerOpen(false)}
        onChange={setForm}
        onSubmit={handleSave}
      />

      <FormConfirmDialog
        open={deleteId != null}
        onCancel={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title={copy.deleteTitle}
        message={copy.deleteMessage}
        confirmLabel={copy.delete}
        cancelLabel={copy.cancel}
      />
    </FormSection>
  );
}
